/**
 * Équipe BCL — accès SUPER-ADMIN UNIQUEMENT (RequireRole côté router).
 * CRUD complet des comptes admin / super_admin / technicien.
 * Gère désactivation logique + suppression définitive avec garde-fous.
 */
import type { Permission, PublicUser, UserRole } from '@bcl/shared';
import { PERMISSION_CATALOG } from '@bcl/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldCheck, Trash2, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { DashboardLayout } from '@/components/dashboard/dashboard-layout';
import {
  createStaff,
  deactivateStaff,
  deleteStaff,
  listStaff,
  reactivateStaff,
  updateStaff,
} from '@/features/admin/admin-api';
import { extractErrorMessage } from '@/features/auth/error-message';
import { useAuth } from '@/lib/auth-context';

const roleOptions: { value: 'admin' | 'super_admin' | 'technicien'; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'super_admin', label: 'Super-admin' },
  { value: 'technicien', label: 'Technicien' },
];

const roleBadgeClass: Record<UserRole, string> = {
  super_admin: 'bg-bcl-blue/10 text-bcl-blue',
  admin: 'bg-blue-100 text-blue-800',
  technicien: 'bg-emerald-100 text-emerald-800',
  partenaire: 'bg-purple-100 text-purple-800',
  client: 'bg-gray-100 text-gray-700',
};

export function AdminStaffPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const staff = useQuery({ queryKey: ['admin', 'staff'], queryFn: listStaff });

  const [form, setForm] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
    role: 'admin' as 'admin' | 'super_admin' | 'technicien',
    permissions: [] as Permission[],
  });
  const [pendingDelete, setPendingDelete] = useState<PublicUser | null>(null);
  const [pendingPermissions, setPendingPermissions] = useState<PublicUser | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['admin', 'staff'] });
    qc.invalidateQueries({ queryKey: ['admin', 'stats'] });
  };

  const createMut = useMutation({
    // Les permissions ne concernent que le rôle admin (le back ignore le reste).
    mutationFn: () =>
      createStaff({
        ...form,
        permissions: form.role === 'admin' ? form.permissions : [],
      }),
    onSuccess: () => {
      toast.success('Compte créé');
      invalidate();
      setForm({
        email: '',
        password: '',
        firstName: '',
        lastName: '',
        phone: '',
        role: 'admin',
        permissions: [],
      });
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const updatePermissionsMut = useMutation({
    mutationFn: ({ userId, permissions }: { userId: string; permissions: Permission[] }) =>
      updateStaff(userId, { permissions }),
    onSuccess: () => {
      toast.success('Permissions mises à jour');
      setPendingPermissions(null);
      invalidate();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const updateRoleMut = useMutation({
    mutationFn: ({
      userId,
      role,
    }: {
      userId: string;
      role: 'admin' | 'super_admin' | 'technicien';
    }) => updateStaff(userId, { role }),
    onSuccess: () => {
      toast.success('Rôle mis à jour');
      invalidate();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const deactivateMut = useMutation({
    mutationFn: (userId: string) => deactivateStaff(userId),
    onSuccess: () => {
      toast.success('Compte désactivé');
      invalidate();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const reactivateMut = useMutation({
    mutationFn: (userId: string) => reactivateStaff(userId),
    onSuccess: () => {
      toast.success('Compte réactivé');
      invalidate();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  const deleteMut = useMutation({
    mutationFn: (userId: string) => deleteStaff(userId),
    onSuccess: () => {
      toast.success('Compte supprimé');
      setPendingDelete(null);
      invalidate();
    },
    onError: (e) => toast.error(extractErrorMessage(e)),
  });

  return (
    <DashboardLayout variant="admin" title="Équipe BCL">
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Comptes staff</CardTitle>
            <CardDescription>
              {staff.data
                ? `${staff.data.length} comptes (admin + super-admin + technicien)`
                : 'Chargement…'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {staff.data?.length ? (
                  staff.data.map((s) => (
                    <StaffRow
                      key={s.id}
                      staff={s}
                      isMe={s.id === user?.id}
                      onChangeRole={(role) =>
                        updateRoleMut.mutate({ userId: s.id, role })
                      }
                      onDeactivate={() => deactivateMut.mutate(s.id)}
                      onReactivate={() => reactivateMut.mutate(s.id)}
                      onAskDelete={() => setPendingDelete(s)}
                      onEditPermissions={() => setPendingPermissions(s)}
                    />
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-sm text-gray-500">
                      Aucun compte staff.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Créer un compte
            </CardTitle>
            <CardDescription>
              Crée un compte admin, super-admin ou technicien.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="firstName">Prénom</Label>
                <Input
                  id="firstName"
                  value={form.firstName}
                  onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lastName">Nom</Label>
                <Input
                  id="lastName"
                  value={form.lastName}
                  onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Téléphone</Label>
              <Input
                id="phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Mot de passe initial</Label>
              <Input
                id="password"
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="role">Rôle</Label>
              <select
                id="role"
                value={form.role}
                onChange={(e) =>
                  setForm({
                    ...form,
                    role: e.target.value as 'admin' | 'super_admin' | 'technicien',
                  })
                }
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                {roleOptions.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </div>
            {form.role === 'admin' && (
              <div className="space-y-2 rounded-md border border-dashed p-3">
                <Label className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-bcl-blue" />
                  Permissions accordées
                </Label>
                <p className="text-xs text-gray-500">
                  Cochez ce que cet admin pourra consulter et gérer. Le
                  super-admin a tout par défaut.
                </p>
                <PermissionsChecklist
                  value={form.permissions}
                  onChange={(permissions) => setForm({ ...form, permissions })}
                  idPrefix="create"
                />
              </div>
            )}
            <Button
              className="w-full"
              disabled={
                !form.email ||
                !form.password ||
                !form.firstName ||
                !form.lastName ||
                !form.phone ||
                createMut.isPending
              }
              onClick={() => createMut.mutate()}
            >
              {createMut.isPending ? 'Création…' : 'Créer le compte'}
            </Button>
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer définitivement ce compte ?</DialogTitle>
            <DialogDescription>
              Cette action est irréversible. Le compte
              {pendingDelete
                ? ` ${pendingDelete.firstName} ${pendingDelete.lastName} (${pendingDelete.email})`
                : ''}{' '}
              sera supprimé de la base.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPendingDelete(null)}
            >
              Annuler
            </Button>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() =>
                pendingDelete && deleteMut.mutate(pendingDelete.id)
              }
            >
              {deleteMut.isPending ? 'Suppression…' : 'Supprimer définitivement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PermissionsDialog
        staff={pendingPermissions}
        isPending={updatePermissionsMut.isPending}
        onClose={() => setPendingPermissions(null)}
        onSave={(permissions) =>
          pendingPermissions &&
          updatePermissionsMut.mutate({ userId: pendingPermissions.id, permissions })
        }
      />
    </DashboardLayout>
  );
}

export function PermissionsChecklist({
  value,
  onChange,
  idPrefix,
}: {
  value: Permission[];
  onChange: (permissions: Permission[]) => void;
  idPrefix: string;
}) {
  const toggle = (key: Permission, checked: boolean) => {
    onChange(
      checked ? [...value, key] : value.filter((p) => p !== key),
    );
  };
  return (
    <div className="space-y-3">
      {PERMISSION_CATALOG.map((group) => (
        <div key={group.label} className="space-y-1.5">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {group.label}
          </p>
          {group.permissions.map((perm) => {
            const id = `${idPrefix}-${perm.key}`;
            return (
              <label
                key={perm.key}
                htmlFor={id}
                className="flex cursor-pointer items-center gap-2 text-sm"
              >
                <Checkbox
                  id={id}
                  checked={value.includes(perm.key)}
                  onCheckedChange={(c) => toggle(perm.key, c === true)}
                />
                {perm.label}
              </label>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function PermissionsDialog({
  staff,
  isPending,
  onClose,
  onSave,
}: {
  staff: PublicUser | null;
  isPending: boolean;
  onClose: () => void;
  onSave: (permissions: Permission[]) => void;
}) {
  const [draft, setDraft] = useState<Permission[]>([]);
  // Réinitialise le brouillon à chaque ouverture sur un nouveau compte.
  const [lastId, setLastId] = useState<string | null>(null);
  if (staff && staff.id !== lastId) {
    setLastId(staff.id);
    setDraft(staff.permissions);
  }
  return (
    <Dialog open={staff !== null} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Permissions de {staff?.firstName} {staff?.lastName}
          </DialogTitle>
          <DialogDescription>
            Cochez ce que cet admin peut consulter et gérer dans le back-office.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto pr-1">
          <PermissionsChecklist value={draft} onChange={setDraft} idPrefix="edit" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Annuler
          </Button>
          <Button disabled={isPending} onClick={() => onSave(draft)}>
            {isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StaffRow({
  staff,
  isMe,
  onChangeRole,
  onDeactivate,
  onReactivate,
  onAskDelete,
  onEditPermissions,
}: {
  staff: PublicUser;
  isMe: boolean;
  onChangeRole: (role: 'admin' | 'super_admin' | 'technicien') => void;
  onDeactivate: () => void;
  onReactivate: () => void;
  onAskDelete: () => void;
  onEditPermissions: () => void;
}) {
  return (
    <TableRow>
      <TableCell className="font-medium">
        {staff.firstName} {staff.lastName}
        {isMe && <span className="ml-2 text-xs text-gray-500">(vous)</span>}
      </TableCell>
      <TableCell>{staff.email}</TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={roleBadgeClass[staff.role]}>
            {staff.role.replace('_', ' ')}
          </Badge>
          {!isMe && staff.active && (
            <select
              value={staff.role}
              onChange={(e) =>
                onChangeRole(e.target.value as 'admin' | 'super_admin' | 'technicien')
              }
              className="h-7 rounded-md border border-input bg-background px-1 text-xs"
              aria-label="Changer le rôle"
            >
              {roleOptions.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          )}
        </div>
      </TableCell>
      <TableCell>
        {staff.active ? (
          <Badge variant="outline" className="bg-green-100 text-green-800">
            Actif
          </Badge>
        ) : (
          <Badge variant="secondary">Désactivé</Badge>
        )}
      </TableCell>
      <TableCell className="text-right">
        {!isMe && (
          <div className="flex justify-end gap-1">
            {staff.active && staff.role === 'admin' && (
              <Button
                size="sm"
                variant="outline"
                onClick={onEditPermissions}
                aria-label="Modifier les permissions"
              >
                <ShieldCheck className="h-4 w-4" />
              </Button>
            )}
            {staff.active ? (
              <Button size="sm" variant="outline" onClick={onDeactivate}>
                Désactiver
              </Button>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={onReactivate}>
                  Réactiver
                </Button>
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={onAskDelete}
                  aria-label="Supprimer définitivement"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        )}
      </TableCell>
    </TableRow>
  );
}
